import React, { ChangeEvent, Dispatch, ReactElement, SetStateAction, useState } from 'react';

import { base64ToImage } from '../../../../../utils/util';
import { APPROVED, DECLINED } from '../../../../../utils/constants';

import { Member, Post } from '../../../../../types/entity.types';
import { RadioOrSelectOption } from '../../../../../types/common.types';

import TribePostContainer from '../../../../../containers/programme/tribe/tribe-post/TribePost.container';

import ProgrammeLayout from '../../../../../layouts/programme/ProgrammeLayout';
import Dropdown from '../../../../common/input/dropdown/Dropdown';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';
import { ReactComponent as SearchIcon } from '../../../../../resources/icons/search.svg';
import { ReactComponent as UserIcon } from '../../../../../resources/icons/user.svg';
import { ReactComponent as DotsIcon } from '../../../../../resources/icons/three-dots.svg';

import './tribe-approvals.scss';

type Props = {
  status: string;
  posts?: Post[];
  members?: Member[];
  filter: RadioOrSelectOption;
  setFilter: Dispatch<SetStateAction<RadioOrSelectOption>>;
  viewApprovalsForUser: (userReference: string, callbackFn: () => void) => void;
  updateAllPosts: (userReference: string, status: string, callbackFn: () => void) => void;
  updateAllComments: (userReference: string, status: string, callbackFn: () => void) => void;
  navigateBack: () => void;
};

const TribeApprovals = ({
  status,
  posts = [],
  members = [],
  filter,
  setFilter,
  viewApprovalsForUser,
  updateAllPosts,
  updateAllComments,
  navigateBack
}: Props): ReactElement => {
  const [searchString, setSearchString] = useState<string>('');
  const [selectedSystemUserExternalReference, setSelectedSystemUserExternalReference] = useState<string | undefined>(
    undefined
  );

  const filterOptions: RadioOrSelectOption[] = [
    { label: 'All Awaiting Approval', value: 'all:pending approval' },
    { label: 'All Posts Awaiting Approval', value: 'post:pending approval' },
    { label: 'All Comments Awaiting Approval', value: 'comment:pending approval' },
    { label: 'All Approved', value: 'all:approved' },
    { label: 'All Approved Posts', value: 'post:approved' },
    { label: 'All Approved Comments', value: 'comment:approved' },
    { label: 'All Declined', value: 'all:declined' },
    { label: 'All Declined Posts', value: 'post:declined' },
    { label: 'All Declined Comments', value: 'comment:declined' }
  ];

  const handleMemberClick = (systemUserExternalReference: string): void => {
    if (systemUserExternalReference === selectedSystemUserExternalReference) {
      setSelectedSystemUserExternalReference(undefined);
    } else {
      setSelectedSystemUserExternalReference(systemUserExternalReference);
    }
  };

  const handleApproveAllPostsClick = (): void => {
    updateAllPosts(selectedSystemUserExternalReference ?? '', APPROVED, (): void =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleDeclineAllPostsClick = (): void => {
    updateAllPosts(selectedSystemUserExternalReference ?? '', DECLINED, (): void =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleApproveAllCommentsClick = (): void => {
    updateAllComments(selectedSystemUserExternalReference ?? '', APPROVED, (): void =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleDeclineAllCommentsClick = (): void => {
    updateAllComments(selectedSystemUserExternalReference ?? '', DECLINED, (): void =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleViewAllClick = (): void => {
    viewApprovalsForUser(selectedSystemUserExternalReference ?? '', (): void =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const renderMembers = (): ReactElement[] => {
    return members.map((member: Member, index: number): ReactElement => {
      const {
        memberName,
        systemUserExternalReference = '',
        postsPendingApproval = 0,
        commentsPendingApproval = 0,
        profilePicture
      }: Partial<Member> = member;

      return (
        <div className="tribe-approvals__members__member" key={index} data-testid={'tribe-approvals-member'}>
          <div className="tribe-approvals__members__member__details">
            <div className="tribe-approvals__members__member__details__profile-picture">
              {profilePicture && <img src={base64ToImage(profilePicture)} alt="" />}
              {!profilePicture && <UserIcon />}
            </div>
            <p className="tribe-approvals__members__member__details__name">{memberName}</p>
            {postsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__posts">
                {postsPendingApproval} {postsPendingApproval > 1 ? 'Posts' : 'Post'}
              </p>
            )}
            {postsPendingApproval > 0 && commentsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__divider">·</p>
            )}
            {commentsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__comments">
                {commentsPendingApproval} {commentsPendingApproval > 1 ? 'Comments' : 'Comment'}
              </p>
            )}
          </div>
          <DotsIcon
            className="tribe-approvals__members__member__dots-icon"
            onClick={(): void => handleMemberClick(systemUserExternalReference)}
            data-testid={'tribe-approvals-member-options'}
          />
          {selectedSystemUserExternalReference === systemUserExternalReference && (
            <div className="tribe-approvals__members__member__options">
              {postsPendingApproval > 0 && <p onClick={handleApproveAllPostsClick}>Approve all posts</p>}
              {postsPendingApproval > 0 && <p onClick={handleDeclineAllPostsClick}>Decline all posts</p>}
              {commentsPendingApproval > 0 && <p onClick={handleApproveAllCommentsClick}>Approve all comments</p>}
              {commentsPendingApproval > 0 && <p onClick={handleDeclineAllCommentsClick}>Decline all comments</p>}
              <p onClick={handleViewAllClick}>View all by {memberName}</p>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <ProgrammeLayout>
      <div className="tribe-approvals" data-testid={`tribe-approvals-${status}`}>
        <div className="tribe-approvals__header">
          <ArrowIcon className="tribe-approvals__header__arrow-icon" onClick={navigateBack} data-testid="arrow-icon" />
          <h1>Posts/Comments Awaiting Approval</h1>
        </div>
        <div className="tribe-approvals__content">
          <div className="tribe-approvals__search">
            <SearchIcon className="tribe-approvals__search__icon" />
            <input
              className="tribe-approvals__search__input"
              value={searchString}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setSearchString(target.value)}
              data-testid={'tribe-approvals-search'}
            />
          </div>

          <div className="tribe-approvals__members-header">
            <p>Members</p>
          </div>

          <div className="tribe-approvals__posts">
            <div className="tribe-approvals__posts__filter" data-testid={'tribe-approvals-filter'}>
              <p>See</p>
              <Dropdown
                name="post-sort-dropdown"
                value={filter}
                options={filterOptions}
                onChange={(option: RadioOrSelectOption): void => setFilter(option)}
                inline
              />
            </div>
            {posts.map(
              (post: Post, index: number): ReactElement => (
                <TribePostContainer key={index} post={post} status={status} approval />
              )
            )}
          </div>

          <div className="tribe-approvals__members">{renderMembers()}</div>
        </div>
      </div>
    </ProgrammeLayout>
  );
};

export default TribeApprovals;
